import { PropsWithChildren, useEffect, useMemo } from 'react';
import { Modal, Platform, StyleSheet } from 'react-native';

import { IconClose, Pressable, Text, XStack, YStack } from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import { ToastProvider } from '@fhs-legacy/native-base';

import { tokens } from '../../tokens';

import { ActionSheetScrollView } from './action-sheet-scroll-view';
import {
  getDialogAccessibilityLabel,
  getModalCloseAccessibilityLabel,
} from './dialog-accessibility';
import { ActionSheetProps } from './types';
import { useFocusWebAriaModalOnOpen } from './use-focus-web-aria-modal-on-open';
import { useSuppressWebRootAccessibility } from './use-suppress-web-root-accessibility';

const NoScrollableDrawer = ({ children, ...props }: PropsWithChildren) => (
  <YStack style={[styles.content]} {...props}>
    {children}
  </YStack>
);

export const DesktopOptionSelectModal = ({
  headerTitle = '',
  sheetAccessibilityLabel,
  hideHeader,
  sheetAriaDescribedBy,
  onClose,
  isVisible,
  children,
  onRequestClose,
  drawerProps,
  footer,
  isScrollableDrawer,
}: ActionSheetProps) => {
  const dialogA11yLabel = useMemo(
    () => getDialogAccessibilityLabel(sheetAccessibilityLabel, headerTitle),
    [sheetAccessibilityLabel, headerTitle]
  );
  const closeA11yLabel = useMemo(
    () => getModalCloseAccessibilityLabel(sheetAccessibilityLabel, headerTitle),
    [sheetAccessibilityLabel, headerTitle]
  );

  const dialogLabel = headerTitle?.trim() ?? '';
  const showHeaderRow = Boolean(dialogLabel) && !hideHeader;
  const shouldFocusWebDialog =
    Boolean(isVisible) &&
    (Boolean(dialogLabel) || Boolean(sheetAccessibilityLabel?.trim()));

  useSuppressWebRootAccessibility(Boolean(isVisible));
  useFocusWebAriaModalOnOpen(shouldFocusWebDialog);

  const handleRequestCloseRef = useBoxRef(() => {
    onClose();
  });

  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {
      return;
    }
    const dismiss = () => {
      (onRequestClose ?? handleRequestCloseRef.current)?.();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismiss();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }
  }, [isVisible, onRequestClose, handleRequestCloseRef]);

  const Drawer = isScrollableDrawer ? ActionSheetScrollView : NoScrollableDrawer;

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onRequestClose ?? handleRequestCloseRef.current}
    >
      <ToastProvider noOverlayContainer>
        <YStack style={styles.container}>
          <Pressable
            accessible={false}
            focusable={false}
            tabIndex={-1}
            // @ts-expect-error RN web — backdrop not in tab order or SR tree
            aria-hidden
            onPress={handleRequestCloseRef.current}
            style={styles.overlay}
          />
          <YStack
            accessibilityLabel={dialogA11yLabel}
            accessibilityViewIsModal
            {...(Platform.OS === 'web'
              ? ({
                  'data-fhs-action-sheet-dialog': 'true',
                  tabIndex: -1,
                  // @ts-expect-error RN web maps role to ARIA role
                  role: 'dialog',
                  'aria-label': dialogA11yLabel,
                  ...(sheetAriaDescribedBy?.trim()
                    ? { 'aria-describedby': sheetAriaDescribedBy.trim() }
                    : {}),
                  'aria-modal': true,
                } as Record<string, unknown>)
              : {
                  // @ts-expect-error web-only dialog semantics
                  accessibilityRole: 'dialog',
                  // @ts-expect-error web-only
                  'aria-modal': true,
                })}
            style={[styles.modalWrapper]}
          >
            {showHeaderRow ? (
              <XStack accessible={false} style={styles.header}>
                <Text accessible={false} style={styles.headerTitle} weight="semibold">
                  {dialogLabel}
                </Text>
                <Pressable
                  accessibilityHint="Closes this sheet"
                  accessibilityLabel={closeA11yLabel}
                  accessibilityRole="button"
                  onPress={onClose}
                  {...(Platform.OS === 'web'
                    ? ({
                        'data-fhs-action-sheet-dismiss': 'true',
                        'aria-label': closeA11yLabel,
                      } as Record<string, unknown>)
                    : {})}
                >
                  <IconClose
                    size={32}
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                  />
                </Pressable>
              </XStack>
            ) : null}
            <Drawer {...drawerProps}>{children}</Drawer>
            {footer}
          </YStack>
        </YStack>
      </ToastProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    backgroundColor: tokens.colors.$blackOpacity75,
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 28,
    paddingTop: 28,
  },
  headerTitle: {
    fontSize: 24,
    lineHeight: 33.6,
  },
  modalWrapper: {
    display: 'flex',
    width: 520,
    maxWidth: '100%',
    maxHeight: '100%',
    backgroundColor: tokens.colors.$white,
    gap: 28,
    borderRadius: 12,
  },
  content: {
    flex: 1,
  },
});
